import { useMemo } from "react";

/**
 * Extract index name from symbol
 * @param {string} symbol - e.g., "NSE:BANKNIFTY25NOV58000PE"
 * @returns {string|null} - e.g., "BANKNIFTY" or null for non-index symbols
 */
function getIndexFromSymbol(symbol, strikeLabel) {
  try {
    if (!symbol) return null;
    const symbolUpper = symbol.toUpperCase();

    // Exclude FUT symbols
    if (symbolUpper.endsWith("FUT")) return null;

    let base = null;
    if (symbolUpper.includes("BSE:SENSEX")) base = "SENSEX";
    else if (symbolUpper.includes("NSE:BANKNIFTY")) base = "BANKNIFTY";
    else if (symbolUpper.includes("NSE:MIDCPNIFTY")) base = "MIDCPNIFTY";
    else if (symbolUpper.includes("NSE:FINNIFTY")) base = "FINNIFTY";
    else if (symbolUpper.includes("NSE:NIFTY") && !symbolUpper.includes("BANKNIFTY") && !symbolUpper.includes("FINNIFTY") && !symbolUpper.includes("MIDCPNIFTY")) base = "NIFTY";

    if (!base) return null;

    // Normalise the strike label:
    //   "strike_2", "strike_3" … → real numbered leg, append to base
    //   "strike_1", "" (empty), "n/a", undefined → treat as strike_1
    const normalised = (strikeLabel || "strike_1").toString().trim().toLowerCase();
    const isRealStrike =
      normalised !== "n/a" &&
      normalised !== "strike_1" &&
      /^strike_\d+$/i.test(normalised);

    if (isRealStrike) {
      return `${base}_${normalised.toUpperCase()}`;
    }
    // Bare base (no strike suffix) — returned for N/A or non-matching labels
    return base;
  } catch (e) {
    console.error("Error extracting index from symbol:", symbol, e);
    return null;
  }
}


/**
 * Normalize order/position side to 'buy' | 'sell' | null
 * Accepts numeric (1,2,-1), strings ('1','2','-1','BUY','SELL') and returns 'buy' or 'sell'
 */
function normalizeSide(rawSide) {
  if (rawSide === null || rawSide === undefined) return null;
  const s = String(rawSide).trim().toLowerCase();
  if (s === "1" || s === "buy" || s === "b") return "buy";
  if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
  return null;
}

/**
 * Calculate MODE (most frequent value) from an array of numbers
 * @param {number[]} numbers
 * @returns {number}
 */
function calculateMode(numbers) {
  if (!numbers || numbers.length === 0) return 0;

  const frequency = {};
  let maxFreq = 0;
  let mode = numbers[0];

  numbers.forEach((num) => {
    frequency[num] = (frequency[num] || 0) + 1;
    if (frequency[num] > maxFreq) {
      maxFreq = frequency[num];
      mode = num;
    }
  });

  return mode;
}

/**
 * Helper: Get multiplier from userMappings for a given client and index
 */
function getMultiplier(userMappings, clientId, index, validMaUnderlyings = ["NIFTY", "BANKNIFTY"], users = []) {
  if ((!userMappings && !users) || !clientId || !index) return 1; // Default to 1 to avoid division by zero/null
  const mapping = userMappings?.find((u) => u.client_id === clientId);
  const matchedUser = users?.find((u) => (u.client_id || u.clientId)?.toString().trim() === clientId?.toString().trim());
  if (!mapping && !matchedUser) return 1;

  const baseIndex = index.split('_')[0].toUpperCase();
  const isStrike2OrAbove = index.toLowerCase().includes('_strike_') && !index.toLowerCase().includes('_strike_1');
  const productVal = (matchedUser?.product || mapping?.product || "").toLowerCase();
  const multiAsset = Number(
    mapping?.multi_asset_multiplier ??
    mapping?.multiasset_multiplier ??
    matchedUser?.multi_asset_multiplier ??
    matchedUser?.multiasset_multiplier ??
    0
  );
  const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
  const effectiveMaMul = multiAsset > 0 ? multiAsset : (Number(mapping?.multiplier || matchedUser?.multiplier) || 1);

  // Multi-asset only applies to Strike 1 / base index of valid underlyings
  if (isMultiAsset) {
    if (isStrike2OrAbove) {
      return 0; // NEVER consider strike 2 or 3 in multi-asset
    }
    if (validMaUnderlyings.includes(baseIndex)) {
      return effectiveMaMul;
    }
    return 0;
  }

  // First try to get the exact index multiplier (e.g. nifty_strike_3_multiplier)
  let exactKey = `${index.toLowerCase()}_multiplier`;
  let val = Number(mapping[exactKey]);

  // If exact strike multiplier is missing or 0, fallback to base index multiplier (only for non-strike2+)
  if (!val || val <= 0) {
    if (isStrike2OrAbove) {
      return 0;
    }
    let baseMultiplierKey;
    switch (baseIndex) {
      case "NIFTY":
        baseMultiplierKey = "multiplier"; // Updated to use 'multiplier' field
        break;
      case "BANKNIFTY":
        baseMultiplierKey = "banknifty_multiplier";
        break;
      case "FINNIFTY":
        baseMultiplierKey = "finnifty_multiplier";
        break;
      case "MIDCPNIFTY":
        baseMultiplierKey = "midcpnifty_multiplier";
        break;
      case "SENSEX":
        baseMultiplierKey = "sensex_multiplier";
        break;
      default:
        baseMultiplierKey = `${baseIndex.toLowerCase()}_multiplier`;
    }
    val = Number(mapping[baseMultiplierKey]);
  }

  return val > 0 ? val : 1; // Return 1 if multiplier is 0 or invalid to act as fallback
}

/**
 * Like getMultiplier but returns 0 (NOT 1) when no mapping or value is found.
 * Used for cross-client anomaly detection so that clients with no multiplier
 * configured for an index are excluded from the comparison entirely.
 */
function getRawMultiplier(userMappings, clientId, index, validMaUnderlyings = ["NIFTY", "BANKNIFTY"], users = []) {
  if ((!userMappings && !users) || !clientId || !index) return 0;
  const mapping = userMappings?.find((u) => u.client_id === clientId);
  const matchedUser = users?.find((u) => (u.client_id || u.clientId)?.toString().trim() === clientId?.toString().trim());
  if (!mapping && !matchedUser) return 0;

  const baseIndex = index.split('_')[0].toUpperCase();
  const isStrike2OrAbove = index.toLowerCase().includes('_strike_') && !index.toLowerCase().includes('_strike_1');
  const productVal = (matchedUser?.product || mapping?.product || "").toLowerCase();
  const multiAsset = Number(
    mapping?.multi_asset_multiplier ??
    mapping?.multiasset_multiplier ??
    matchedUser?.multi_asset_multiplier ??
    matchedUser?.multiasset_multiplier ??
    0
  );
  const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
  const effectiveMaMul = multiAsset > 0 ? multiAsset : (Number(mapping?.multiplier || matchedUser?.multiplier) || 1);

  // Multi-asset only applies to Strike 1 / base index of valid underlyings
  if (isMultiAsset) {
    if (isStrike2OrAbove) {
      return 0; // NEVER consider strike 2 or 3 in multi-asset
    }
    if (validMaUnderlyings.includes(baseIndex)) {
      return effectiveMaMul;
    }
    return 0;
  }

  let exactKey = `${index.toLowerCase()}_multiplier`;
  let val = Number(mapping[exactKey]);

  // For strike-specific indexes (e.g. NIFTY_STRIKE_2, NIFTY_STRIKE_3),
  // do NOT fall back to the base index multiplier.
  // A client must have an explicit strike multiplier > 0 to be included.
  if (isStrike2OrAbove || index.toLowerCase().includes('_strike_')) {
    return val > 0 ? val : 0;
  }

  // For base indexes, try well-known base multiplier key as fallback
  if (!val || val <= 0) {
    let baseMultiplierKey;
    switch (baseIndex) {
      case "NIFTY":        baseMultiplierKey = "multiplier"; break;
      case "BANKNIFTY":    baseMultiplierKey = "banknifty_multiplier"; break;
      case "FINNIFTY":     baseMultiplierKey = "finnifty_multiplier"; break;
      case "MIDCPNIFTY":   baseMultiplierKey = "midcpnifty_multiplier"; break;
      case "SENSEX":       baseMultiplierKey = "sensex_multiplier"; break;
      default:             baseMultiplierKey = `${baseIndex.toLowerCase()}_multiplier`;
    }
    val = Number(mapping[baseMultiplierKey]);
  }

  return val > 0 ? val : 0; // 0 means "not configured" — caller should exclude this client
}

/**
 * Calculate base quantity for each unique symbol+strategy combination
 * NOW NORMALIZED by client multipliers to find the true "1x" base qty
 * @param {Array} positions - Position data
 * @param {Array} userMappings - User mappings to lookup multipliers
 * @returns {Object} - { "symbol_strategy": baseQty, ... }
 */
function calculateSymbolStrategyBaseQty(positions, userMappings) {
  const symbolStrategyQtyMap = {};

  positions.forEach((position) => {
    const index = getIndexFromSymbol(position.symbol, position.strike_label);

    // Skip non-index positions
    if (!index) return;

    // Skip closed positions (net_qty = 0)
    const netQty = position.net_qty || position.qty || 0;
    if (Math.abs(netQty) === 0) {
      return;
    }

    const key = `${position.symbol}_${position.strategy || "NO_STRATEGY"}`;

    if (!symbolStrategyQtyMap[key]) {
      symbolStrategyQtyMap[key] = {
        symbol: position.symbol,
        strategy: position.strategy,
        indices: [], // Store all indices seen
        normalizedQuantities: [], // Store normalized quantities
      };
    }

    const clientId = position.client_id;
    const qty = Math.abs(netQty);
    const multiplier = getMultiplier(userMappings, clientId, index);
    const normalizedQty = qty / multiplier;

    symbolStrategyQtyMap[key].normalizedQuantities.push(normalizedQty);
    symbolStrategyQtyMap[key].indices.push(index);
  });

  // Calculate MODE of NORMALIZED quantities and MODE of indices
  const baseQtyMap = {};
  Object.entries(symbolStrategyQtyMap).forEach(([key, data]) => {
    const modeQty = calculateMode(data.normalizedQuantities);
    
    // Find most common index (mode)
    const frequency = {};
    let maxFreq = 0;
    let modeIndex = data.indices[0];
    data.indices.forEach((idx) => {
      frequency[idx] = (frequency[idx] || 0) + 1;
      if (frequency[idx] > maxFreq) {
        maxFreq = frequency[idx];
        modeIndex = idx;
      }
    });

    baseQtyMap[key] = {
      symbol: data.symbol,
      strategy: data.strategy,
      index: modeIndex, // Consensus index
      baseQty: modeQty, // True 1x base qty
      clients: data.normalizedQuantities.length,
      allQtys: data.normalizedQuantities,
    };
  });

  return baseQtyMap;
}

/**
 * Group and sum quantities by client, index, and side
 * @param {Array} positions - Position data
 * @param {Object} symbolStrategyBaseQty - Symbol+strategy base qty map containing consensus index
 * @returns {Object} - Grouped data
 */
function groupPositionsByClientIndexSide(positions, symbolStrategyBaseQty) {
  const grouped = {};

  positions.forEach((position) => {
    const keySymbolStrategy = `${position.symbol}_${position.strategy || "NO_STRATEGY"}`;
    const baseQtyInfo = symbolStrategyBaseQty?.[keySymbolStrategy];
    const index = baseQtyInfo ? baseQtyInfo.index : getIndexFromSymbol(position.symbol, position.strike_label);

    // Skip non-index positions (stocks, MCX, etc.)
    if (!index) {
      return;
    }

    // Positions don't have status - they represent actual holdings
    // If a position has net_qty of 0, it means it's closed and should be filtered out
    const netQty = position.net_qty || position.qty || 0;
    if (Math.abs(netQty) === 0) {
      // console.log(`🚫 Skipping closed position (qty=0) for ${position.symbol}`);
      return;
    }

    const key = `${position.client_id}_${index}_${position.side}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: position.client_id,
        index,
        side: position.side,
        qty: 0,
        pnl: 0,
        positions: [],
      };
    }

    // Use net_qty from WebSocket data, fallback to qty
    const qty = Math.abs(netQty);
    // Use pl from WebSocket data, fallback to pnl
    const pnl = position.pl || position.pnl || 0;

    grouped[key].qty += qty;
    grouped[key].pnl += pnl;
    grouped[key].positions.push(position);
  });

  return grouped;
}

/**
 * Group and sum sell orders by client, index, and side
 * @param {Array} orders - Orders data
 * @param {Array} positions - Position data to derive precise symbol mapping
 * @param {Object} symbolStrategyBaseQty - Symbol+strategy base qty map containing consensus index
 * @returns {Object} - Grouped sell orders
 */
function groupSellOrdersByClientIndexSide(orders, positions, symbolStrategyBaseQty) {
  const grouped = {};

  // Filter only sell orders (tolerant normalization)
  const sellOrders = orders.filter(
    (order) => normalizeSide(order.side) === "sell"
  );

  // Build two lookup maps from active positions, keyed by symbol+strategy
  // (mirrors the PositionComparisonModal matching logic).
  //
  // Map 1: clientId_symbol_strategy → index  (most precise: exact client match)
  // Map 2: symbol_strategy → index           (global fallback: any client that holds
  //         the same symbol+strategy gives us the correct strike bucket, because
  //         the same symbol+strategy always maps to the same index column)
  //
  // Using symbol+strategy (not just symbol) ensures that two different strategies
  // on the same symbol that belong to different strike columns are handled correctly.
  const positionIndexMap = {};     // clientId_symbol_strategy → index
  const symbolStrategyIndexMap = {}; // symbol_strategy → index  (global fallback)
  const symbolOnlyIndexMap = {};     // symbol → index  (broadest fallback, ignores strategy)

  if (positions) {
    positions.forEach((p) => {
      const stratKey = (p.strategy || "NO_STRATEGY").toString().trim().toUpperCase();
      const keySymbolStrategy = `${p.symbol}_${stratKey}`;
      const baseQtyInfo = symbolStrategyBaseQty?.[`${p.symbol}_${p.strategy || "NO_STRATEGY"}`];
      const idx = baseQtyInfo ? baseQtyInfo.index : getIndexFromSymbol(p.symbol, p.strike_label);
      if (idx) {
        const clientId = p.client_id || p.clientId;
        // Client-specific key (most accurate)
        positionIndexMap[`${clientId}_${keySymbolStrategy}`] = idx;
        // Global fallback — first-seen wins (safe since same symbol+strategy is
        // always in the same index bucket)
        if (!symbolStrategyIndexMap[keySymbolStrategy]) {
          symbolStrategyIndexMap[keySymbolStrategy] = idx;
        }
        // Broadest fallback: symbol only (ignores strategy mismatch between order and position)
        // Prefer strike-specific over base — only set if not already set to a strike-specific key
        const existing = symbolOnlyIndexMap[p.symbol];
        if (!existing || !existing.includes('_STRIKE_')) {
          symbolOnlyIndexMap[p.symbol] = idx;
        }
      }
    });
  }

  // console.log(
  //   `🔍 [DEBUG] Filtered ${sellOrders.length} sell orders from ${orders.length} total orders`
  // );

  sellOrders.forEach((order) => {
    const clientId = order.clientId || order.client_id;
    const stratKey = (order.strategy || "NO_STRATEGY").toString().trim().toUpperCase();
    const orderSymbolStratKey = `${order.symbol}_${stratKey}`;

    // Step 1: match by clientId + symbol + strategy (mirrors PositionComparisonModal logic)
    let index = positionIndexMap[`${clientId}_${orderSymbolStratKey}`];

    // Step 2: any client's position with the same symbol+strategy tells us the bucket
    if (!index) {
      index = symbolStrategyIndexMap[orderSymbolStratKey];
    }

    // Step 2.5: broadest fallback — same symbol from any position regardless of strategy
    // (covers orders whose strategy field is slightly different or missing)
    if (!index) {
      index = symbolOnlyIndexMap[order.symbol];
    }

    // Step 3: last-resort — parse from order's own strike_label field
    if (!index) {
      index = getIndexFromSymbol(order.symbol, order.strike_label);
    }

    const normalized = normalizeSide(order.side) || String(order.side);
    const key = `${clientId}_${index}_${normalized}`;

    if (!grouped[key]) {
      grouped[key] = {
        client_id: clientId,
        index,
        side: normalized,
        qty: 0,
      };
    }

    grouped[key].qty += Math.abs(order.qty || 0);
  });

  return grouped;
}

/**
 * Determine color based on validation rules
 * Position View: Only check if Position QTY matches SL QTY
 * (No base qty comparison - that's for Order View only)
 * @param {number} positionQty
 * @param {number} slQty
 * @returns {string} - 'green' or 'red'
 */
function determineColor(positionQty, slQty) {
  // Check if Position qty matches SL qty
  if (positionQty !== slQty) {
    return "red";
  }

  // Condition passes (GREEN)
  return "green";
}

/**
 * Hook for Position View data processing
 * @param {Array} positions - Position data from WebSocket
 * @param {Array} orders - Orders data from API
 * @param {Array} userMappings - User mapping data with multipliers
 * @param {number} positionsUpdateVersion - Version counter to force re-computation
 * @returns {Object} - { data, baseQtyMap, activeIndexes }
 */
export function usePositionsView(
  positions,
  orders,
  userMappings,
  positionsUpdateVersion,
  multiAssetUnderlyings = ["NIFTY", "BANKNIFTY"],
  users = []
) {
  const positionViewData = useMemo(() => {
    // If no positions, return empty
    if (!positions || positions.length === 0) {
      // console.log("⚠️ No positions data");
      return { data: [], baseQtyMap: {}, activeIndexes: [] };
    }

    // console.log("🔄 Processing positions view:", {
    //   positionsCount: positions.length,
    //   ordersCount: orders?.length || 0,
    //   userMappingsCount: userMappings?.length || 0,
    //   version: positionsUpdateVersion,
    // });

    // Log sample data for debugging
    if (positions.length > 0) {
      // console.log("📦 Sample position:", positions[0]);
    }
    if (userMappings && userMappings.length > 0) {
      // console.log("👤 Sample user mapping:", userMappings[0]);
      // console.log(
      //   "👥 All client_ids in user mappings:",
      //   userMappings.map((u) => u.client_id)
      // );
    }

    // Step 1: Calculate symbol+strategy base qty first
    const symbolStrategyBaseQty = calculateSymbolStrategyBaseQty(positions, userMappings);

    // Step 2: Group positions by client, index, and side using consensus index
    const groupedPositions = groupPositionsByClientIndexSide(positions, symbolStrategyBaseQty);
    // console.log("📊 Grouped positions:", groupedPositions);

    // Step 3: Group sell orders by client, index, and side (if orders available) using consensus index
    const groupedSellOrders = orders
      ? groupSellOrdersByClientIndexSide(orders, positions, symbolStrategyBaseQty)
      : {};
    if (orders) {
      // console.log("🛒 Grouped sell orders:", groupedSellOrders);
      // console.log("📋 Sample order:", orders[0]);
    } else {
      console.log("⏳ Orders not loaded yet, SL QTY will be 0");
    }

    // Step 3: Create client-wise index data
    const clientIndexMap = {};

    Object.values(groupedPositions).forEach((posGroup) => {
      const clientId = posGroup.client_id;
      const index = posGroup.index;

      if (!clientIndexMap[clientId]) {
        clientIndexMap[clientId] = {};
      }

      if (!clientIndexMap[clientId][index]) {
        clientIndexMap[clientId][index] = {
          qty: 0,
          slQty: 0,
          pnl: 0,
          positions: [],
        };
      }

      // Aggregate position data
      clientIndexMap[clientId][index].qty += posGroup.qty;
      clientIndexMap[clientId][index].pnl += posGroup.pnl;
      clientIndexMap[clientId][index].positions = (
        clientIndexMap[clientId][index].positions || []
      ).concat(posGroup.positions || []);

      // Find matching sell order qty (use normalized side key)
      const sellOrderKey = `${clientId}_${index}_sell`;
      if (groupedSellOrders[sellOrderKey]) {
        clientIndexMap[clientId][index].slQty =
          groupedSellOrders[sellOrderKey].qty;
        // console.log(
        //   `✅ Matched SL for ${clientId} ${index}:`,
        //   groupedSellOrders[sellOrderKey].qty
        // );
      } else {
        //console.log(`❌ No SL match for key: ${sellOrderKey}`);
      }
    });

    // Step 4: Add Orphans from Sell Orders (Orders without Positions)
    Object.values(groupedSellOrders).forEach(orderGroup => {
      const { client_id, index, qty } = orderGroup;
      // Skip null index (filtered out NFO/BFO)
      if (!index) return;

      if (!clientIndexMap[client_id]) {
        clientIndexMap[client_id] = {};
      }

      // Guard: if the resolved index is a bare base (e.g. "NIFTY") but the client
      // already has strike-specific entries for that base (e.g. "NIFTY_STRIKE_2"),
      // skip creating the bare entry — it would produce a phantom "NIFTY STRIKE 1" column.
      const baseOfIndex = index.split('_')[0]; // e.g. "NIFTY" from "NIFTY" or "NIFTY_STRIKE_2"
      const isBaseKey = !index.includes('_STRIKE_'); // true for "NIFTY", false for "NIFTY_STRIKE_2"
      if (isBaseKey) {
        const clientKeys = Object.keys(clientIndexMap[client_id] || {});
        const hasStrikeSpecific = clientKeys.some(
          k => k.startsWith(`${baseOfIndex}_STRIKE_`)
        );
        if (hasStrikeSpecific) {
          // This order belongs to one of the strike-specific columns; don't inject a bare base column.
          return;
        }
      }

      // If index entry doesn't exist, create it (it's an orphan)
      if (!clientIndexMap[client_id][index]) {
        clientIndexMap[client_id][index] = {
          qty: 0,
          slQty: qty, // Set the SL Qty
          pnl: 0
        };
      }
      // If it exists, slQty was already handled in Step 3
    });

    // console.log("🗂️ Client Index Map:", clientIndexMap);

    const validMaUnderlyings = (Array.isArray(multiAssetUnderlyings) && multiAssetUnderlyings.length > 0)
      ? multiAssetUnderlyings.map(u => String(u).toUpperCase().trim())
      : ["NIFTY", "BANKNIFTY"];

    // Step 5: Build final position view data with color coding
    const data = [];

    Object.keys(clientIndexMap).forEach((clientId) => {
      // Prioritize Dhan XTS mapping if multiple exist for the same client_id
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        // Find XTS specifically, or fallback to the first one found (e.g. SS)
        userMapping = matches.find(u => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      if (!userMapping) {
        if (userMappings && userMappings.length > 0) {
          // console.warn(`⚠️ No user mapping found for client_id: "${clientId}"`);
          // console.log(
          //   "Available user mappings:",
          //   userMappings.map((u) => u.client_id)
          // );
        }
        // Still show the position with placeholder name
      }

      const matchedUser = users?.find(u => (u.client_id || u.clientId)?.toString().trim() === clientId?.toString().trim());
      const productVal = (matchedUser?.product || userMapping?.product || "").toLowerCase();
      const multiAssetMul = Number(
        userMapping?.multi_asset_multiplier ??
        userMapping?.multiasset_multiplier ??
        matchedUser?.multi_asset_multiplier ??
        matchedUser?.multiasset_multiplier ??
        0
      );
      const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
      const effectiveMaMul = multiAssetMul > 0 ? multiAssetMul : (Number(userMapping?.multiplier || matchedUser?.multiplier) || 1);

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : (matchedUser?.name || `Client ${clientId}`),
        status: userMapping ? userMapping.status : (matchedUser?.status || "unknown"),
        broker: userMapping ? userMapping.broker : (matchedUser?.broker || "unknown"),
        multi_asset_multiplier: multiAssetMul,
        effectiveMaMul,
        isMultiAsset,
        product: isMultiAsset ? 'multi_asset' : 'optimus',
      };

      const baseIndexes = [
        "NIFTY",
        "BANKNIFTY",
        "FINNIFTY",
        "MIDCPNIFTY",
        "SENSEX",
      ];
      const indexes = Array.from(new Set([...baseIndexes, ...Object.keys(clientIndexMap[clientId] || {})]));

      indexes.forEach((index) => {
        // Only show index data if the client actually has positions in that index
        if (clientIndexMap[clientId][index]) {
          let multiplier = 0;
          const baseIndex = index.split('_')[0].toUpperCase();
          const isStrike2OrAbove = index.toLowerCase().includes('_strike_') && !index.toLowerCase().includes('_strike_1');

          if (isMultiAsset) {
            if (isStrike2OrAbove) {
              multiplier = 0; // NEVER consider strike 2 or 3 in multi-asset
            } else if (validMaUnderlyings.includes(baseIndex)) {
              multiplier = effectiveMaMul;
            } else {
              multiplier = 0;
            }
          } else {
            // 1. Try exact strike multiplier (e.g., nifty_strike_3_multiplier)
            let exactKey = `${index.toLowerCase()}_multiplier`;
            multiplier = userMapping ? Number(userMapping[exactKey]) || 0 : 0;

            // 2. If missing or 0, fallback to base index multiplier (only for non-strike2+)
            if ((!multiplier || multiplier <= 0) && !isStrike2OrAbove) {
              let baseMultiplierKey;
              switch (baseIndex) {
                case "NIFTY":
                  baseMultiplierKey = "multiplier"; // Updated to use 'multiplier' field
                  break;
                case "BANKNIFTY":
                  baseMultiplierKey = "banknifty_multiplier";
                  break;
                case "FINNIFTY":
                  baseMultiplierKey = "finnifty_multiplier";
                  break;
                case "MIDCPNIFTY":
                  baseMultiplierKey = "midcpnifty_multiplier";
                  break;
                case "SENSEX":
                  baseMultiplierKey = "sensex_multiplier";
                  break;
                default:
                  baseMultiplierKey = `${baseIndex.toLowerCase()}_multiplier`;
              }
              multiplier = userMapping ? Number(userMapping[baseMultiplierKey]) || 0 : 0;
            }
          }
          const indexData = clientIndexMap[clientId][index];

          const color = determineColor(indexData.qty, indexData.slQty);

          rowData[index.toLowerCase()] = {
            multiplier,
            qty: indexData.qty,
            slQty: indexData.slQty,
            pnl: indexData.pnl,
            positions: indexData.positions || [], // Include raw positions for LiveAggregatedPNLCell
            color,
            expectedQty: 0, // Not used in position view
          };
        }
      });

      // Calculate Total Multiplier
      let totalMultiplier = 0;
      indexes.forEach((index) => {
        const lowerIndex = index.toLowerCase();
        if (clientIndexMap[clientId][index]) {
            const indexDataRow = rowData[lowerIndex];
            if (indexDataRow) {
                totalMultiplier += (indexDataRow.multiplier || 0);
            }
        }
      });
      rowData.totalMultiplier = totalMultiplier;

      data.push(rowData);
    });

    // Determine active indexes
    const baseIndexNames = ["nifty", "banknifty", "finnifty", "midcpnifty", "sensex"];
    const allPossibleKeys = new Set();
    data.forEach(row => {
      Object.keys(row).forEach(k => allPossibleKeys.add(k));
    });
    
    const activeIndexes = Array.from(allPossibleKeys).filter(k => 
      baseIndexNames.includes(k.split('_')[0]) && data.some(row => row[k] !== undefined)
    );

    // Calculate base qty for each index (MODE of NORMALIZED quantities) separated by regime
    const optimusBaseQtyMap = {};
    const optimusMaBaseQtyMap = {};
    const baseQtyMap = {};

    activeIndexes.forEach((index) => {
      const optimusNormalizedQtys = data
        .filter((row) => !row.isMultiAsset)
        .map((row) => {
          const rawQty = row[index]?.qty;
          const multiplier = row[index]?.multiplier || 1;
          return rawQty ? rawQty / (multiplier > 0 ? multiplier : 1) : undefined;
        })
        .filter((qty) => qty !== undefined && qty > 0);

      const maNormalizedQtys = data
        .filter((row) => row.isMultiAsset)
        .map((row) => {
          const rawQty = row[index]?.qty;
          const multiplier = row[index]?.multiplier || 1;
          return rawQty ? rawQty / (multiplier > 0 ? multiplier : 1) : undefined;
        })
        .filter((qty) => qty !== undefined && qty > 0);

      if (optimusNormalizedQtys.length > 0) {
        optimusBaseQtyMap[index] = calculateMode(optimusNormalizedQtys);
      }
      if (maNormalizedQtys.length > 0) {
        optimusMaBaseQtyMap[index] = calculateMode(maNormalizedQtys);
      }
      baseQtyMap[index] = optimusBaseQtyMap[index] || optimusMaBaseQtyMap[index] || 0;
    });

    // =========================================================
    // Cross-Client Anomaly Map
    // Separately for Optimus and Multi-Asset regimes
    // =========================================================
    const crossClientAnomalyMap = {};

    // Build a per-client per-strategy actual-qty lookup from raw positions
    // key: clientId → { "symbol_strategy" → totalQty }
    const clientStrategyActualQty = {};
    positions.forEach((p) => {
      const qty = Math.abs(Number(p.net_qty || p.qty || 0));
      if (qty === 0) return;
      const clientId = p.client_id;
      const key = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
      if (!clientStrategyActualQty[clientId]) clientStrategyActualQty[clientId] = {};
      clientStrategyActualQty[clientId][key] =
        (clientStrategyActualQty[clientId][key] || 0) + qty;
    });

    activeIndexes.forEach((index) => {
      // Collect per-client info — only include clients with multiplier >= 1
      const clientEntries = data.map((row) => {
        const multiplier = getRawMultiplier(userMappings, row.clientId, index, validMaUnderlyings, users);

        // Exclude clients that don't trade this index (multiplier 0 or not configured)
        if (!multiplier || multiplier < 1) {
          return { clientId: row.clientId, isMultiAsset: row.isMultiAsset, excluded: true };
        }

        const rawQty = row[index]?.qty;
        const norm =
          rawQty !== undefined && rawQty !== null
            ? rawQty / multiplier
            : null; // null = has multiplier>=1 but no open position

        return { clientId: row.clientId, isMultiAsset: row.isMultiAsset, norm, multiplier, excluded: false };
      });

      const checkGroupConsensus = (groupEntries) => {
        const included = groupEntries.filter((c) => !c.excluded);
        if (included.length < 2) return;

        const presentNorms = included
          .filter((c) => c.norm !== null)
          .map((c) => c.norm);
        if (presentNorms.length === 0) return;

        const consensusQty = calculateMode(presentNorms);

        // Only strategies actually held by this product group (Optimus or Multi-Asset).
        // symbolStrategyBaseQty is global, so an Optimus-only name such as HHPC_PC5
        // must not be reported as missing on Multi-Asset clients.
        const multiplierByClient = {};
        const groupClientIds = [];
        included.forEach((c) => {
          multiplierByClient[c.clientId] = c.multiplier;
          groupClientIds.push(c.clientId);
        });

        const groupStrategyBase = {};
        Object.entries(symbolStrategyBaseQty).forEach(([key, info]) => {
          if (!info.index || info.index.toLowerCase() !== index.toLowerCase()) return;

          const norms = [];
          groupClientIds.forEach((clientId) => {
            const actualQty = clientStrategyActualQty[clientId]?.[key] || 0;
            const clientMultiplier = multiplierByClient[clientId];
            if (actualQty <= 0 || !clientMultiplier || clientMultiplier < 1) return;
            norms.push(actualQty / clientMultiplier);
          });
          if (norms.length === 0) return;

          groupStrategyBase[key] = {
            strategy: info.strategy,
            symbol: info.symbol,
            baseQty: calculateMode(norms),
          };
        });

        included.forEach(({ clientId, norm, multiplier, excluded }) => {
          if (excluded || norm === consensusQty) return;

          const strategyIssues = [];
          Object.entries(groupStrategyBase).forEach(([key, info]) => {
            const expectedQty = Math.round(info.baseQty * multiplier);
            const actualQty = clientStrategyActualQty[clientId]?.[key] || 0;
            if (actualQty !== expectedQty) {
              strategyIssues.push({
                strategy: info.strategy || "N/A",
                symbol: info.symbol,
                expectedQty,
                actualQty,
                missing: actualQty === 0,
              });
            }
          });

          if (!crossClientAnomalyMap[clientId]) {
            crossClientAnomalyMap[clientId] = {};
          }
          crossClientAnomalyMap[clientId][index] = {
            consensusQty,
            actualNorm: norm,
            multiplier,
            strategyIssues,
          };
        });
      };

      // Run anomaly check separately within Optimus and Multi-Asset groups
      checkGroupConsensus(clientEntries.filter(c => !c.isMultiAsset));
      checkGroupConsensus(clientEntries.filter(c => c.isMultiAsset));
    });

    return { data, baseQtyMap, optimusBaseQtyMap, optimusMaBaseQtyMap, activeIndexes, symbolStrategyBaseQty, crossClientAnomalyMap };
  }, [positions, orders, userMappings, multiAssetUnderlyings, users]); // REMOVED positionsUpdateVersion to stop re-render on LTP

  return positionViewData;
}
